import { isDevMode } from "@angular/core";
import * as loglevel from "loglevel";

export type LogMessageContent = any[];

export interface LoggingContextInfo {
  methodName: loglevel.LogLevelNames;
  level: loglevel.LogLevelNumbers;
  loggerName: string | symbol;
}

export type PrefixCallback = (
  context: LoggingContextInfo,
) => string | Array<any>;

export type LogPrefix = ReturnType<PrefixCallback> | PrefixCallback;

export type MutatorCallback = (
  context: LoggingContextInfo,
  ...message: LogMessageContent
) => any[];

/**
 * strict key-typing for Object.fromEntries
 */
function fromEntries<K extends string | number | symbol, V>(
  entries: Iterable<readonly [K, V]>,
): Record<K, V> {
  return Object.fromEntries(entries) as Record<K, V>;
}

const logLevels: loglevel.LogLevel & Record<string, number> = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  SILENT: 5,
};

/**
 * Look up the LogLevelNumber for any LogLevelDesc
 */
const levelForDesc: Record<loglevel.LogLevelDesc, loglevel.LogLevelNumbers> =
  fromEntries(
    Object.entries(logLevels).flatMap(
      ([uc, n]) =>
        [[uc, n] as const, [uc.toLowerCase(), n] as const, [n, n] as const] as [
          (
            | Uppercase<keyof loglevel.LogLevel | "silent">
            | Lowercase<keyof loglevel.LogLevel | "silent">
            | loglevel.LogLevelNumbers
          ),
          loglevel.LogLevelNumbers,
        ][],
    ),
  );

/**
 * Expose `loglevel.getLogger(name)` functionality with some extra optional behaviours
 *
 * @param name The name of the produced logger
 * @param options.levelMaxOf A list of loglevel expressions or Logger objects;
 *          if present, setDefaultLevel on produced logger to maximum
 *          (least-verbose) of levels (current loglevel of Loggers)
 * @param options.baseFactory Use specified MethodFactory (or get from
 *          logger) as base
 * @param options.mutate Callback to modify messages handled by this logger
 */
export function getLogger(
  name: Parameters<(typeof loglevel)["getLogger"]>[0],
  options?: {
    levelMaxOf?: (loglevel.LogLevelDesc | loglevel.Logger)[];
    baseFactory?: loglevel.Logger | loglevel.MethodFactory;
    mutate?: MutatorCallback;
  },
): loglevel.Logger {
  const logger = loglevel.getLogger(name);
  const levels = options?.levelMaxOf?.map((l) =>
    typeof l === "object" ? l.getLevel() : levelForDesc[l],
  );

  if (levels?.length) {
    logger.setDefaultLevel(Math.max(...levels) as loglevel.LogLevelNumbers);
  }

  if (options?.baseFactory !== undefined) {
    const base = options.baseFactory;
    if (typeof base === "function") {
      logger.methodFactory = base.bind(logger);
    } else {
      logger.methodFactory = base.methodFactory.bind(logger);
    }
  }

  if (options?.mutate) {
    const mutate = options.mutate;
    const baseFactory = logger.methodFactory;

    logger.methodFactory = MessageMutator._createFactory(baseFactory, mutate);
  }
  logger.rebuild();
  return logger;
}

export abstract class MessageMutator {
  public static _createFactory(
    baseFactory: loglevel.MethodFactory,
    messageCallback: (
      context: LoggingContextInfo,
      ...message: LogMessageContent
    ) => LogMessageContent,
  ): loglevel.MethodFactory {
    return (methodName, level, loggerName): loglevel.LoggingMethod => {
      const context = { methodName, level, loggerName };
      return (...message: any[]) =>
        baseFactory(
          methodName,
          level,
          loggerName,
        )(...messageCallback(context, ...message));
    };
  }

  /**
   * Factory for MutatorCallback that prefixes message content
   *
   * @param prefix Prefix for messages
   * @param options.stringConcat
   *        "prepend": If prefix and first item of message content are both strings, concat the prefix to the item instead of as a separate item.
   *        "multiline": Like prepend, but if first message content item contains line breaks, prepend prefix on each line.
   *        false: Always insert prefix as separate item(s) in message.
   */
  public static AddPrefix(
    prefix: LogPrefix,
    options?: { stringConcat?: "prepend" | "multiline" | false },
  ): MutatorCallback {
    const concat = options?.stringConcat ?? "prepend";

    return function (
      context: {
        methodName: loglevel.LogLevelNames;
        level: loglevel.LogLevelNumbers;
        loggerName: string | symbol;
      },
      ...message: LogMessageContent
    ): LogMessageContent {
      const p = typeof prefix === "function" ? prefix(context) : prefix;

      // cook down the case where it's an array with a single string
      const pfx =
        Array.isArray(p) && p.length === 1 && typeof p[0] === "string"
          ? p[0]
          : p;

      switch (true) {
        case concat !== false &&
          typeof message[0] === "string" &&
          typeof pfx === "string":
          return [
            message[0].replace(concat === "multiline" ? /^/gm : /^/, pfx + " "),
            ...message.slice(1),
          ];
        case typeof pfx === "string":
          return [pfx, ...message];
        default:
          return [...pfx, ...message];
      }
    };
  }
}

/**
 * Create subsidiary logger for separating extra debug detail logging.
 *
 * @param name Logger name
 * @param basedOn Parent to base loglevel and methodfactory on
 * @param options.enable Request 'debug' level if true;
 *          'silent' if false. Masked by level of basedOn.
 *          Default: true
 * @param options.prefix Prepend to messages from this logger;
 *          null disables prefixing.
 *          Default: '[V]'
 */
export function verboseLogger(
  name: string | symbol,
  basedOn: loglevel.Logger,
  options?: { enable?: boolean; prefix?: LogPrefix | null },
): loglevel.Logger {
  const enable = options?.enable ?? true;
  const prefix = options?.prefix === undefined ? "[V]" : options.prefix;

  return getLogger(name, {
    levelMaxOf: [basedOn, enable ? "debug" : "silent"],
    baseFactory: basedOn,
    mutate: prefix ? MessageMutator.AddPrefix(prefix) : undefined,
  });
}

/** Replacement for default 'console' logging service */
export const console = getLogger("app");
console.setDefaultLevel(isDevMode() ? "trace" : "warn");
console.resetLevel();

export const swLog = getLogger("app-sw-updates");
swLog.setDefaultLevel("info");
swLog.resetLevel();
